import { useEffect, useState } from "react";
import { effectiveTheme, toggleTheme, type Theme } from "../lib/theme";

/**
 * The light / dark switch, beside the account menu (Jonathan, 2026-10-20:
 * "a dark and light theme toggle ... in the main page header next to the login
 * section"). The colours are the ones from the approved redesign artifact; this
 * is only the control. The work of swapping tokens is `lib/theme.ts` + the
 * `[data-theme]` blocks in styles.css.
 *
 * A sun when dark is showing (press to go light), a moon when light is showing
 * (press to go dark) — the icon is the thing you'd switch TO, the convention
 * most of these controls use. `onDark` matches the other top-bar controls on
 * the navy phone bar.
 *
 * Two of these can be mounted at once (desktop deskbar + phone top bar), but
 * only ever one is on screen at a given width, so each keeps its own state
 * rather than sharing a store.
 */
export default function ThemeToggle({ onDark = false }: { onDark?: boolean }) {
  const [theme, setTheme] = useState<Theme>(() => effectiveTheme());

  // Track the OS while the viewer has made no explicit choice, so the icon is
  // right if the system flips under us (and on the other mounted instance).
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const sync = () => setTheme(effectiveTheme());
    mq.addEventListener?.("change", sync);
    return () => mq.removeEventListener?.("change", sync);
  }, []);

  const dark = theme === "dark";
  return (
    <button
      type="button"
      className={`theme-toggle ${onDark ? "on-dark" : ""}`}
      aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
      title={dark ? "Switch to light theme" : "Switch to dark theme"}
      onClick={() => setTheme(toggleTheme())}
    >
      {dark ? (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
          <circle cx="8" cy="8" r="3.1" />
          <path d="M8 1.5v1.6M8 12.9v1.6M1.5 8h1.6M12.9 8h1.6M3.4 3.4l1.1 1.1M11.5 11.5l1.1 1.1M12.6 3.4l-1.1 1.1M4.5 11.5l-1.1 1.1" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M13.2 9.6A5.4 5.4 0 0 1 6.4 2.8a5.4 5.4 0 1 0 6.8 6.8Z" />
        </svg>
      )}
    </button>
  );
}
